import { type FC, memo } from "react";

import { type ProductData } from "~/domains/product/types/ProductData";
import Spacer from "~/common/components/Spacer";
import { Carousel } from "~/common/components/carousel/Carousel";
import classNames from "classnames";

import { MobileScrollableTileContainer } from "~/features/productDetails/components/RelatedProducts/MobileScrollableTileContainer";
import {
  DesktopRelatedProductCard,
  MobileRelatedProductCard,
} from "~/features/productDetails/components/RelatedProducts/RelatedProductCard";
import { Col, Row } from "~/common/components/Layout";
import useMediaQuery from "~/common/hooks/useMediaQuery";
import { MEDIA_QUERY_MIN_DESKTOP_S } from "~/common/utils/screenSizes";
import { useTranslation } from "react-i18next";
import { styled } from "~/common/mui";

export type RelatedProductData = Pick<
  ProductData,
  | "designation"
  | "sapMaterialNumber"
  | "aliases"
  | "shortDescription"
  | "preferred"
  | "url"
  | "images"
  | "manufacturerLogoUrl"
  | "stockAmount"
>;

interface RelatedProductsBlockProps {
  mainProductDesignation: string;
  relatedProducts: RelatedProductData[];
  className?: string;
}

const RelatedProductsBlock: FC<RelatedProductsBlockProps> = ({
  className,
  relatedProducts,
  mainProductDesignation,
}) => {
  const { t } = useTranslation();

  const isDesktop = useMediaQuery(MEDIA_QUERY_MIN_DESKTOP_S);

  if (relatedProducts.length === 0) {
    return null;
  }

  const content = isDesktop ? (
    <Carousel
      data={relatedProducts}
      pageItemsCount={3}
      renderSlide={(data) => (
        <Row>
          {data.data.map((item) => (
            <Col size={4}>
              <DesktopRelatedProductCard
                key={item.sapMaterialNumber}
                product={item}
                mainProductDesignation={mainProductDesignation}
              />
            </Col>
          ))}
        </Row>
      )}
    />
  ) : (
    <MobileScrollableTileContainer
      data={relatedProducts}
      render={(item) => <MobileRelatedProductCard product={item} mainProductDesignation={mainProductDesignation} />}
      getKey={(item) => item.sapMaterialNumber}
    />
  );

  return (
    <RelatedProductsRoot className={classNames("scha__related-products", className)}>
      {isDesktop && <Spacer className={"mb-30"} />}
      <RelatedProductsTitle className={"scha__related-products__title"}>{t("pdp.relatedProducts.title")}</RelatedProductsTitle>
      {content}
    </RelatedProductsRoot>
  );
};

const RelatedProductsRoot = styled("div")(({ theme }) => ({
  marginTop: theme.spacing(4),
}));

const RelatedProductsTitle = styled("h3")(({ theme }) => ({
  marginTop: theme.spacing(3.75),
  marginBottom: theme.spacing(2),
  ...theme.typography.titleLarge,
  [theme.breakpoints.up("desktop-s")]: {
    marginBottom: theme.spacing(3.75),
    marginTop: 0,
  },
}));

export default memo(RelatedProductsBlock);
